import { print } from 'graphql';
import { gql } from 'graphql-tag';
import type { RxCollection, RxDocument, RxJsonSchema } from 'rxdb';

import { AdditionalFields } from '../../models/models';
import { getDateModified, getRoleForMutation } from '../../utilities/utilityFunctions';
import { defaultDoc, getValueFromLocalStorage } from '../share/defaultDoc';

export interface DeficiencyComponentRemoval {
  readonly deficiencyId: string;
  readonly deficiencyUUID: string;
  readonly inspectionId: string;
  readonly isActive: boolean;
  readonly comment: string;
  readonly componentRemovalId?: number;
  readonly parentComponentId: number;
  readonly parentComponentName: string;
  readonly componentRemovalIdBySC: number;
  readonly commentBySC: string;
  readonly siteId: number;
  readonly disciplineId: number;
  readonly dateCreated: string;
  readonly dateModified: string;
  readonly assetId: number;
  readonly componentVersionInspectionId?: number;
  readonly componentVersionId?: number;
  readonly componentVersionUUId: string;
}

export const deficiencyComponentRemovalSchema: RxJsonSchema<DeficiencyComponentRemoval> = {
  type: 'object',
  version: 0,
  description: 'describes component removal',
  primaryKey: 'componentVersionUUId',
  properties: {
    deficiencyId: { type: ['string', 'null'] },
    deficiencyUUID: { type: ['string', 'null'] },
    parentComponentId: { type: ['number', 'null'] },
    componentRemovalId: { type: ['number', 'null'] },
    componentRemovalIdBySC: { type: ['number', 'null'] },
    parentComponentName: { type: ['string', 'null'] },
    commentBySC: { type: ['string', 'null'] },
    comment: { type: ['string', 'null'] },
    assetId: { type: ['number', 'null'] },
    isActive: { type: ['boolean', 'null'] },
    siteId: { type: 'number' },
    disciplineId: { type: 'number' },
    dateCreated: { type: ['string', 'null'] },
    inspectionId: { type: ['string', 'null'] },
    dateModified: { type: ['string', 'null'] },
    componentVersionId: { type: ['number', 'null'] },
    componentVersionInspectionId: { type: ['number', 'null'] },
    componentVersionUUId: { type: 'string', maxLength: 36 },
  },
};

export type DeficiencyComponentRemovalCollection = RxCollection<DeficiencyComponentRemoval> | null;
export type DeficiencyComponentRemovalDocument = RxDocument<DeficiencyComponentRemoval>;

export const deficiencyComponentRemovalPullQueryBuilder = (
  document: DeficiencyComponentRemoval,
) => {
  let doc = document;
  if (!doc) {
    (doc as unknown) = { ...defaultDoc };
  }

  const query = print(gql`
    query mobileComponentRemoval(
      $siteId: Int!
      $disciplineId: Int!
      $lastModified: DateTime!
      $isLeadInspector: Boolean!
    ) {
      mobileComponentRemoval(
        siteId: $siteId
        disciplineId: $disciplineId
        lastModified: $lastModified
        isLeadInspector: $isLeadInspector
      ) {
        deficiencyId
        deficiencyUUID
        inspectionId
        isActive
        comment
        componentRemovalId
        parentComponentId
        parentComponentName
        componentRemovalIdBySC
        commentBySC
        siteId
        disciplineId
        dateCreated
        dateModified
        assetId
        componentVersionInspectionId
        componentVersionId
        componentVersionUUId
      }
    }
  `);

  return {
    query,
    variables: {
      siteId: JSON.parse(getValueFromLocalStorage('selectedSite'))?.selectedSiteId,
      disciplineId: JSON.parse(getValueFromLocalStorage('selectedDiscipline'))
        ?.selectedDisciplineId,
      lastModified: getDateModified(
        doc?.dateModified,
        doc?.dateCreated,
        doc.siteId,
        doc.disciplineId,
        JSON.parse(getValueFromLocalStorage('selectedSite'))?.selectedSiteId,
        JSON.parse(getValueFromLocalStorage('selectedDiscipline'))?.selectedDisciplineId,
      ),
      isLeadInspector: getValueFromLocalStorage('isLeadInspector') === 'True',
    },
  };
};

export const deficiencyComponentRemovalPushQueryBuilder = (doc: DeficiencyComponentRemoval[]) => {
  const document = doc[0];
  const query = print(gql`
    mutation saveMobileComponentRemoval(
      $mobileComponentRemovalInput: GetMobileComponentRemovalInput
      $role: Int!
    ) {
      saveMobileComponentRemoval(
        mobileComponentRemoval: $mobileComponentRemovalInput
        role: $role
      ) {
        assetInsepctionComponentFailureId
      }
    }
  `);
  const { ...data } = document;
  const role = getRoleForMutation(data);

  const variables = {
    mobileComponentRemovalInput: data,
    role,
  };

  return {
    query,
    variables,
  };
};

export const deficiencyComponentRemovalPushModifier = (doc: DeficiencyComponentRemovalDocument) => {
  const obj: AdditionalFields = {
    ...doc,
  };
  const blackListedProp = [
    '_attachments',
    '_rev',
    '_deleted',
    'dateCreated',
    'dateModified',
    '_meta',
    'isDeleted',
  ];
  blackListedProp.forEach(property => {
    obj[property as keyof AdditionalFields] = undefined;
  });
  return obj;
};

export default {};
